/**
 * BootSequence — tela de boas-vindas cinemática do onboarding Dr. Pera IA.
 *
 * Sequência:
 * 1. Fade in fundo ink (via CSS)
 * 2. Logo pera zoom-in + glow expand (1.5s CSS)
 * 3. "THE FUTURE IS NOW" typewriter rápido + scale (aparece em 1.6s)
 * 4. Boot ASCII linha por linha (delay incremental CSS)
 * 5. Botão CTA fade-in pulse (aparece em 4.2s)
 *
 * prefers-reduced-motion: tudo instantâneo.
 */
import React, { useEffect, useState } from 'react';
import '../styles/onboarding.css';

/** Linhas do boot ASCII */
const BOOT_LINES: Array<{ prefix: string; text: string; ok: boolean }> = [
  { prefix: '>', text: ' INITIALIZING DR. PERA IA...', ok: false },
  { prefix: '[OK]', text: ' PROFILE LOADED', ok: true },
  { prefix: '[OK]', text: ' SOAP MODE READY', ok: true },
  { prefix: '[OK]', text: ' PLANTÃO MEMORY 12H ACTIVE', ok: true },
  { prefix: '[OK]', text: ' 7 CLINICAL TOOLS CONNECTED', ok: true },
];

interface BootSequenceProps {
  /** Callback chamado quando médico clica em "Começar" */
  onComplete: () => void;
  /** Função de tradução i18n */
  t: (key: string) => string;
}

/** Detecta preferência por menos animações */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function BootSequence({ onComplete, t }: BootSequenceProps) {
  const [ctaVisible, setCtaVisible] = useState(false);
  const reducedMotion = prefersReducedMotion();

  // Com motion reduzido: mostra CTA imediatamente
  // Sem motion: aguarda a animação CSS (4.2s) terminar
  useEffect(() => {
    if (reducedMotion) {
      setCtaVisible(true);
      return;
    }
    // CSS anima CTA em 4.2s — sincronizamos aqui para evitar blur entre
    // "opacity:0 via CSS" e "display:none via React"
    const timer = setTimeout(() => setCtaVisible(true), 4100);
    return () => clearTimeout(timer);
  }, [reducedMotion]);

  // Atalho teclado: Enter ou Espaço avança
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onComplete();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onComplete]);

  return (
    <div
      className="onb-boot"
      role="dialog"
      aria-modal="true"
      aria-label={t('onboarding.welcome.title')}
    >
      {/* Logo pera Pixar */}
      <img
        src="/icons/icon-128.png"
        alt="Mascote Dr. Pera"
        width={96}
        height={96}
        className={reducedMotion ? '' : 'onb-logo'}
        style={reducedMotion ? {
          filter: 'drop-shadow(0 0 10px rgba(124, 124, 248, 0.55))',
          marginBottom: '28px',
          position: 'relative',
          zIndex: 2,
          // Fix 24/05/2026 Dr. Lu: icone redondo sem fundo quadrado
          borderRadius: '50%',
          objectFit: 'cover',
        } : undefined}
      />

      {/* "THE FUTURE IS NOW" */}
      <div
        className={reducedMotion ? '' : 'onb-headline'}
        style={reducedMotion ? {
          fontFamily: 'var(--font-sans)',
          fontSize: 'clamp(14px, 2.5vw, 22px)',
          color: '#FFFFFF',
          textShadow: 'none',
          letterSpacing: '0.12em',
          textAlign: 'center',
          marginBottom: '32px',
          position: 'relative',
          zIndex: 2,
        } : undefined}
      >
        {t('onboarding.welcome.title')}
      </div>

      {/* Boot ASCII lines */}
      <div className="onb-boot-log" aria-hidden="true">
        {BOOT_LINES.map((line, i) => (
          <div
            key={i}
            className={
              reducedMotion
                ? ''
                : `onb-boot-line${line.ok ? ' onb-boot-line--ok' : ''}`
            }
            style={reducedMotion ? {
              color: line.ok ? 'var(--violet)' : 'var(--ink-mute)',
              fontFamily: 'var(--font-sans)',
              fontSize: '13px',
            } : undefined}
          >
            <span
              style={{
                color: line.ok ? 'var(--violet)' : 'var(--ink-mute)',
                marginRight: '4px',
              }}
            >
              {line.prefix}
            </span>
            {line.text}
          </div>
        ))}
      </div>

      {/* CTA — aparece após animação */}
      {(ctaVisible || reducedMotion) && (
        <button
          type="button"
          className={reducedMotion ? '' : 'onb-cta'}
          onClick={onComplete}
          autoFocus
          style={reducedMotion ? {
            fontFamily: 'var(--font-sans)',
            fontSize: '12px',
            padding: '14px 28px',
            background: 'var(--cta-blue)',
            border: '1px solid var(--cta-blue)',
            color: '#FFFFFF',
            cursor: 'pointer',
            letterSpacing: '0.08em',
            textShadow: 'none',
            boxShadow: 'none',
          } : undefined}
        >
          [ {t('onboarding.welcome.cta')} ]
        </button>
      )}
    </div>
  );
}
